import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { selectSession, putAdvisorConsentsByAdvisorIdRequest } from '@client/model';

import { z } from 'zod';
import { cn } from '@client/lib';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  electronicSignatureAndRecordsConsent,
  authorizationToDiscloseHealthInformation,
  certificationOfTheAccuracyOfHealthInformationDisclosed,
} from '@shared';

type StepType = 'first' | 'last';

const consentStepOneFormSchema = z.object({
  authorizationToDiscloseHealthInformation: z.boolean().refine(value => value === true, {
    message: 'You must agree on Authorization to Disclose Health Information to proceed.',
  }),
  certificationOfTheAccuracyOfHealthInformationDisclosed: z
    .boolean()
    .refine(value => value === true, {
      message:
        'You must agree on Certification of the Accuracy of Health Information Disclosed to proceed.',
    }),
});

function ConsentStepOne({ setActiveStep }: { setActiveStep: (step: StepType) => void }) {
  const form = useForm<z.infer<typeof consentStepOneFormSchema>>({
    mode: 'onChange',
    resolver: zodResolver(consentStepOneFormSchema),
    defaultValues: {
      authorizationToDiscloseHealthInformation: false,
      certificationOfTheAccuracyOfHealthInformationDisclosed: false,
    },
  });

  function onFormSubmit() {
    setActiveStep('last');
  }

  const isDisabled = !form.formState.isValid;

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onFormSubmit)}
        className="flex flex-col gap-4"
      >
        <FormField
          control={form.control}
          name="authorizationToDiscloseHealthInformation"
          render={({ field }) => {
            return (
              <FormItem className="bg-primary/5 border-primary/10 space-y-2 rounded-xl border p-3 sm:p-4">
                <div className="flex items-start gap-2">
                  <FormControl className="translate-y-0.5">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="text-primary-dark !m-0 text-sm font-medium">
                    <FormLabel className="cursor-pointer font-bold">
                      Authorization to Disclose Health Information
                    </FormLabel>{' '}
                    — “I authorize Waterlily and Wealth Assurance Group, LLC, and any reinsurers or
                    third-party service providers they designate, to receive, transmit and store my
                    health information for the purpose of generating insurance quotes and preparing
                    underwriting forms for up to <strong>24 months</strong> from today. I understand
                    I may revoke this authorization at any time by e-mailing{' '}
                    <a
                      href="mailto:hello@joinwaterlily.com"
                      className="text-primary underline"
                    >
                      hello@joinwaterlily.com
                    </a>{' '}
                    except to the extent action has already been taken.”
                  </div>
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <FormField
          control={form.control}
          name="certificationOfTheAccuracyOfHealthInformationDisclosed"
          render={({ field }) => {
            return (
              <FormItem className="bg-primary/5 border-primary/10 space-y-2 rounded-xl border p-4">
                <div className="flex items-start gap-2">
                  <FormControl className="translate-y-0.5">
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <FormLabel className="text-primary-dark !m-0 cursor-pointer text-sm font-medium">
                    I certify that the information provided in this policy application form is true,
                    complete and accurate to the best of my knowledge. I understand that material
                    misstatements or omissions may result in the denial of coverage or rescission of
                    any policy issued.
                  </FormLabel>
                </div>
                <FormMessage />
              </FormItem>
            );
          }}
        />
        <Button
          type="submit"
          className="w-full"
          disabled={isDisabled}
        >
          Agree & Continue
        </Button>
      </form>
    </Form>
  );
}

function ConsentStepTwo() {
  return (
    <div>
      <div>
        <DialogTitle className="mb-3 sm:mb-4 text-balance text-base sm:text-lg leading-normal text-black">
          Long-Term-Care Insurance Suitability & Best-Interest Disclosure
        </DialogTitle>
        <DialogDescription className="text-sm sm:text-base">
          Waterlily and Wealth Assurance Group collect information about your income, assets, goals
          and health to help determine whether any recommended insurance product is suitable and in
          your best interest, as defined by applicable state regulations (NAIC Model #641 and #275;
          NY Reg 187). We will maintain copies of your information and any recommendation for at
          least 7 years and make them available to you or your state Department of Insurance upon
          request.
        </DialogDescription>
      </div>
      <hr className="my-4 sm:my-6" />
      <div>
        <DialogTitle className="mb-3 sm:mb-4 text-balance text-base sm:text-lg leading-normal text-black">
          Electronic Signature & Records Consent
        </DialogTitle>
        <DialogDescription className="text-sm sm:text-base">
          By selecting “I Agree” below, you:
          <ul className="my-0.5 list-disc space-y-0.5 p-4">
            <li>
              Consent to the use of <strong>electronic signatures</strong> and{' '}
              <strong>electronic records</strong>
            </li>
            <li>
              Confirm you have the hardware and software necessary to access PDFs and web pages
            </li>
            <li>
              Understand you may withdraw consent at any time by emailing{' '}
              <a
                href="mailto:hello@joinwaterlily.com"
                className="text-primary underline"
              >
                hello@joinwaterlily.com
              </a>
            </li>
          </ul>
          <strong>Need a paper copy?</strong> Paper copies are available on request at no charge.
        </DialogDescription>
      </div>
    </div>
  );
}

const consentStepTwoFormSchema = z.object({
  electronicSignatureAndRecordsConsent: z.boolean().refine(value => value === true, {
    message: 'You must agree to Electronic Signature and Records Consent to proceed.',
  }),
});

export function ApplicationConsentDialog({
  children,
  clientPolicyApplicationId,
  handleSubmitApplication,
}: {
  children: React.ReactNode;
  clientPolicyApplicationId: string;
  handleSubmitApplication: () => void;
}) {
  const dispatch = useDispatch();
  const session = useSelector(selectSession);
  const [isOpen, setIsOpen] = React.useState(false);
  const [activeStep, setActiveStep] = React.useState<'first' | 'last'>('first');

  const form = useForm<z.infer<typeof consentStepTwoFormSchema>>({
    mode: 'onChange',
    resolver: zodResolver(consentStepTwoFormSchema),
    defaultValues: {
      electronicSignatureAndRecordsConsent: false,
    },
  });

  function handleOpenChange(nextValue: boolean) {
    if (!nextValue) {
      setActiveStep('first');
    }
    setIsOpen(nextValue);
  }

  function onFormSubmit(_data: z.infer<typeof consentStepTwoFormSchema>) {
    if (!session.advisor?.advisorId) return;

    const consents = [
      {
        consentText: authorizationToDiscloseHealthInformation.text,
        consentVersionDescription: `${authorizationToDiscloseHealthInformation.consentVersionDescription} - ${clientPolicyApplicationId}`,
      },
      {
        consentText: certificationOfTheAccuracyOfHealthInformationDisclosed.text,
        consentVersionDescription: `${certificationOfTheAccuracyOfHealthInformationDisclosed.consentVersionDescription} - ${clientPolicyApplicationId}`,
      },
      {
        consentText: electronicSignatureAndRecordsConsent.text,
        consentVersionDescription: `${electronicSignatureAndRecordsConsent.consentVersionDescription} - ${clientPolicyApplicationId}`,
      },
    ];

    // Dispatch the action to save all consents at once
    dispatch(
      putAdvisorConsentsByAdvisorIdRequest({
        advisorId: session.advisor.advisorId,
        consents,
      }),
    );
    
    // Close dialog and proceed with application submission
    setIsOpen(false);
    handleSubmitApplication();
  }

  const isFirstStep = activeStep === 'first';
  const isLastStep = activeStep === 'last';
  const isDisabled = !form.formState.isValid;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleOpenChange}
    >
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className={cn(
          'max-h-[85vh] md:max-h-[90vh] overflow-y-auto rounded-2xl',
          {
            'w-[95vw] max-w-3xl !p-3 sm:!p-4': isFirstStep,
            'w-[95vw] max-w-xl !p-3 sm:!p-4': isLastStep,
          }
        )}
        noCloseTrigger
      >
        {isFirstStep && <ConsentStepOne setActiveStep={setActiveStep} />}
        {isLastStep && <ConsentStepTwo />}
        {isLastStep && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onFormSubmit)}
              className="flex flex-col"
            >
              <FormField
                control={form.control}
                name="electronicSignatureAndRecordsConsent"
                render={({ field }) => {
                  return (
                    <FormItem className="space-y-2">
                      <div className="flex items-start gap-2">
                        <FormControl className="translate-y-0.5">
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <FormLabel className="!m-0 cursor-pointer text-sm font-medium text-gray-700">
                          I have read and agree to the above Electronic Signature & Records Consent
                        </FormLabel>
                      </div>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <Button
                type="submit"
                className="mt-6 w-full"
                disabled={isDisabled}
              >
                I Agree & Submit
              </Button>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  );
}
